/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { pluck, sum } from 'ramda';
import { v4 as uuid } from 'uuid';
import { Button, Icon, Input, Modal, Select, TextBox } from '../..';
import { Tax } from '../../../context/storeContext';
import useValidator from '../../../hooks/useValidator';
import roundUp from '../../../utils/roundUp';

type ModalProps = {
  onCloseModal: () => void;
  addingTaxScheme: boolean;
  currencySymbol: string;
  taxes: Tax[];
  tax?: Tax;
  onAddTax: (newTax: {
    name: string;
    rate: number;
    description: string;
  }) => Promise<unknown>;
};

type TaxGroupings = Array<{ taxId: number; applicableOn: string }>;

type TaxesPreview = Array<{ type: string; amount: number; className: string }>;

const getInitialTaxGroupings = (
  taxes: Tax[],
  groupings?: TaxGroupings | null,
) => {
  if (groupings && Object.keys(groupings).length > 0) {
    const taxGroupings = groupings.map(gTax => ({
      key: uuid(),
      taxesRate: taxes,
      ...gTax,
    }));

    return taxGroupings;
  }

  return [{ key: uuid(), taxesRate: taxes, taxId: NaN, applicableOn: '' }];
};

const initialTaxPreview = [
  {
    type: 'Subtotal',
    amount: 100,
    className: 'text-sm',
  },
];

function TaxAdd({
  onCloseModal,
  currencySymbol,
  onAddTax,
  taxes,
  tax: selectedTax,
  addingTaxScheme,
}: ModalProps) {
  const [tax, setTax] = React.useState({
    name: selectedTax?.name || '',
    rate: selectedTax?.rate || NaN,
    description: selectedTax?.description || '',
    groupings:
      addingTaxScheme ||
      (selectedTax?.groupings && Object.keys(selectedTax?.groupings).length > 0)
        ? getInitialTaxGroupings(taxes, selectedTax?.groupings)
        : [],
  });
  const enableAddTaxScheme = Boolean(
    addingTaxScheme ||
      (selectedTax?.groupings &&
        Object.keys(selectedTax?.groupings).length > 0),
  );
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');
  const [validator, showValidationMessage] = useValidator({});
  const [taxIdError, setTaxIdError] = React.useState<{
    keys: string[];
    error: string;
  }>({ keys: [], error: '' });
  const [applicableOnError, setApplicableOnError] = React.useState<{
    keys: string[];
    error: string;
  }>({ keys: [], error: '' });
  const [previewTaxes, setPreviewTaxes] =
    React.useState<TaxesPreview>(initialTaxPreview);

  React.useEffect(() => {
    let netAndTaxApplies = [] as TaxesPreview;
    let notNetAndTaxApplies = [] as TaxesPreview;

    if (
      tax.groupings.every(
        gTax => !Number.isNaN(gTax.taxId) && gTax.applicableOn,
      )
    ) {
      const subTotal = initialTaxPreview[0]?.amount || 0;

      const otherTaxes = tax.groupings.filter(
        gTax => gTax.applicableOn !== 'net-tax-amount',
      );

      netAndTaxApplies = tax.groupings
        .filter(gTax => gTax.applicableOn === 'net-tax-amount')
        .map(gTax => {
          const findTax = taxes.find(fTax => fTax.id === gTax.taxId);

          const summedOtherRates = sum(
            pluck(
              'rate',
              taxes.filter(fTax =>
                pluck('taxId', otherTaxes).includes(fTax.id),
              ),
            ),
          );

          const rate = findTax?.rate || 0;

          const sumRate = (summedOtherRates * rate) / 100 + rate;

          return {
            type: findTax?.name || '',
            amount: (sumRate / 100) * subTotal,
            className: 'text-sm',
          };
        })
        .filter(gTax => gTax.type);

      notNetAndTaxApplies = otherTaxes
        .map(gTax => {
          const findTax = taxes.find(fTax => fTax.id === gTax.taxId);
          return {
            type: findTax?.name || '',
            amount: ((findTax?.rate || 0) / 100) * subTotal,
            className: 'text-sm',
          };
        })
        .filter(gTax => gTax.type);

      setPreviewTaxes([
        ...initialTaxPreview,
        ...notNetAndTaxApplies,
        ...netAndTaxApplies,
      ]);
    }
  }, [tax.groupings, taxes]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    setTax(fndTax => ({
      ...fndTax,
      [name]: type !== 'number' ? value : (event.target as any)?.valueAsNumber,
    }));
  };

  const handleTaxAdd = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    if (enableAddTaxScheme) {
      if (tax.groupings.find(gTax => Number.isNaN(gTax.taxId))) {
        setTaxIdError({
          keys: pluck(
            'key',
            tax.groupings.filter(gTax => Number.isNaN(gTax.taxId)),
          ),
          error: 'Tax rate is required.',
        });
        return;
      }

      if (tax.groupings.find(gTax => !gTax.applicableOn)) {
        setApplicableOnError({
          keys: pluck(
            'key',
            tax.groupings.filter(gTax => !gTax.applicableOn),
          ),
          error: 'Applicable on is required.',
        });
        return;
      }
    }

    const fndTax = {
      ...tax,
      rate: tax.rate || 0,
      ...(enableAddTaxScheme
        ? {
            groupings: tax.groupings.map(gTax => ({
              taxId: gTax.taxId,
              applicableOn: gTax.applicableOn,
            })),
          }
        : { groupings: [] }),
    };

    setStatus('LOADING');

    await onAddTax(fndTax);

    setStatus('RESOLVED');
  };

  const handleAddTaxRateFieldChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    key: string,
  ) => {
    const { name, value } = event.target;

    if (name === 'taxId') {
      setTaxIdError(fndError => {
        return {
          ...fndError,
          keys: fndError.keys.filter(fndKey => fndKey !== key),
        };
      });
    }

    if (name === 'applicableOn') {
      setApplicableOnError(fndError => {
        return {
          ...fndError,
          keys: fndError.keys.filter(fndKey => fndKey !== key),
        };
      });
    }

    setTax(fndTax => ({
      ...fndTax,
      groupings: [
        ...fndTax.groupings.map(fndGTax => {
          if (fndGTax.key === key) {
            return {
              ...fndGTax,
              [name]: name === 'taxId' ? parseInt(value, 10) : value,
            };
          }
          return {
            ...fndGTax,
            ...(Number.isNaN(fndGTax.taxId) &&
              name === 'taxId' && {
                taxesRate: fndGTax.taxesRate.filter(
                  fndTaxRate => fndTaxRate.id !== parseInt(value, 10),
                ),
              }),
          };
        }),
      ],
    }));
  };

  const handleAddTaxRateField = () => {
    if (tax.groupings.length >= taxes.length) {
      return;
    }

    setTax(fndTax => ({
      ...fndTax,
      groupings: [
        ...fndTax.groupings,
        {
          key: uuid(),
          taxesRate: fndTax.groupings[
            fndTax.groupings.length - 1
          ].taxesRate.filter(
            fndTaxRate =>
              !pluck('taxId', tax.groupings).includes(fndTaxRate.id),
          ),
          taxId: NaN,
          applicableOn: '',
        },
      ],
    }));
  };

  const handleRemoveTaxRateField = ({
    taxId,
    key,
  }: {
    taxId: number;
    key: string;
  }) => {
    const removedTaxRate = taxes.find(gRate => gRate.id === taxId);

    const newGroupings = tax.groupings
      .map((fndGTax, index) => {
        if (index === tax.groupings.length - 1 && removedTaxRate) {
          return {
            ...fndGTax,
            taxesRate: [...fndGTax.taxesRate, removedTaxRate],
          };
        }

        return fndGTax;
      })
      .filter(fndGTax => fndGTax.key !== key);

    setTax(fndTax => ({
      ...fndTax,
      groupings: newGroupings,
    }));
  };

  return (
    <Modal
      onClose={onCloseModal}
      show
      className={`${
        enableAddTaxScheme ? 'tablet:!w-[600px]' : 'tablet:!w-[500px]'
      }`}
    >
      <div className="rounded-md bg-white w-full h-full tablet:max-h-[700px] overflow-auto">
        <form className="flex flex-col h-full">
          <div className="flex-col p-5 flex-1 pb-[115px]">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              {selectedTax
                ? 'Update Tax'
                : enableAddTaxScheme
                ? 'New Tax Scheme'
                : 'Add New Tax'}
            </h3>

            {enableAddTaxScheme ? (
              <div className="text-pos-black text-sm mt-2 mb-2 font-normal">
                <div className="flex flex-row space-x-8">
                  <div className="flex-1 space-y-4">
                    <div>
                      <label htmlFor="name" className="text-xbase block mb-1">
                        Taxing Scheme Name{' '}
                        <span className="text-xs font-light text-pos-red">
                          *
                        </span>
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={tax.name}
                        onChange={handleInputChange}
                      />
                      {validator.message('name', tax.name, 'required|min:2', {
                        messages: {
                          required: 'Tax scheme name is required.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      })}
                    </div>
                    <div>
                      <label
                        htmlFor="description"
                        className="text-xbase block mb-1"
                      >
                        Description
                      </label>
                      <TextBox
                        id="description"
                        name="description"
                        value={tax.description}
                        rows={2}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="w-[40%] shrink-0">
                    <h3>Preview</h3>
                    <div className="flex flex-col space-y-2 bg-[#e9ecef] px-4 py-3 rounded-md mt-1 min-h-[160px]">
                      <div className="flex-1 space-y-2 text-pos-gray">
                        {previewTaxes.map(pTax => (
                          <div className="flex flex-row justify-between space-x-2">
                            <p className={`font-medium ${pTax.className}`}>
                              {pTax.type}
                            </p>
                            <span>
                              {currencySymbol}
                              {roundUp(pTax.amount, 2).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="w-full h-fit">
                        <hr className="border-[#d0d7de] mb-2" />
                        <div className="flex flex-row justify-between space-x-2">
                          <p className="font-semibold text-base">Total</p>
                          <span className="text-base">
                            {currencySymbol}
                            {roundUp(
                              sum(pluck('amount', previewTaxes)),
                              2,
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-b border-[#d0d7de] mt-4 mb-3">
                  <h2 className="text-xbase font-semibold inline-flex items-center">
                    Assign Taxes{' '}
                    <span className="ml-1 text-xs font-light text-pos-red">
                      *
                    </span>
                  </h2>
                </div>

                <div className="flex flex-col w-full items-start space-y-3">
                  {tax.groupings.map((gTax, index) => (
                    <div
                      className="flex flex-row space-x-6 w-full items-start"
                      key={gTax.key}
                    >
                      <div className="flex-1 w-full">
                        <label
                          htmlFor="taxId"
                          className="text-xbase block mb-1"
                        >
                          Tax Rate
                        </label>
                        <Select
                          placeholder={{
                            value: '',
                            label: 'Select tax rate',
                          }}
                          value={gTax.taxId || ''}
                          options={gTax.taxesRate.map(fndTax => ({
                            value: fndTax.id,
                            label: fndTax.name,
                          }))}
                          name="taxId"
                          id="taxId"
                          error={Boolean(
                            taxIdError && taxIdError.keys.includes(gTax.key),
                          )}
                          onChange={event =>
                            handleAddTaxRateFieldChange(event, gTax.key)
                          }
                        />
                        {taxIdError && taxIdError.keys.includes(gTax.key) && (
                          <div className="text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1">
                            {taxIdError.error}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 w-full">
                        <label
                          htmlFor="applicableOn"
                          className="text-xbase block mb-1"
                        >
                          Applicable on
                        </label>
                        <Select
                          placeholder={{
                            value: '',
                            label: 'Apply tax on',
                          }}
                          value={gTax.applicableOn}
                          options={[
                            {
                              value: 'net-amount',
                              label: 'Net amount',
                            },
                            {
                              value: 'tax-amount',
                              label: 'Tax amount',
                            },
                            {
                              value: 'net-tax-amount',
                              label: 'Net + Tax amount',
                            },
                          ]}
                          name="applicableOn"
                          id="applicableOn"
                          error={Boolean(
                            applicableOnError &&
                              applicableOnError.keys.includes(gTax.key),
                          )}
                          onChange={event =>
                            handleAddTaxRateFieldChange(event, gTax.key)
                          }
                        />
                        {applicableOnError &&
                          applicableOnError.keys.includes(gTax.key) && (
                            <div className="text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1">
                              {applicableOnError.error}
                            </div>
                          )}
                      </div>
                      {((selectedTax?.groupings &&
                        Object.keys(selectedTax?.groupings).length > 0) ||
                        index !== 0) &&
                        tax.groupings.length > 1 && (
                          <div className="shrink-0 h-full relative !ml-0">
                            <span className="text-xbase block mb-1 invisible">
                              Close
                            </span>
                            <div className="py-[6px] px-[0.75rem] h-[42.8px] border border-transparent flex items-center justify-center">
                              <Icon
                                onClick={() =>
                                  handleRemoveTaxRateField({
                                    taxId: gTax.taxId,
                                    key: gTax.key,
                                  })
                                }
                                id="close"
                                className="w-[8px] h-[8px] stroke-black fill-black stroke-2 cursor-pointer"
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  ))}

                  {tax.groupings.length < taxes.length && (
                    <button
                      type="button"
                      onClick={handleAddTaxRateField}
                      className="text-pos-hyperlink mt-3"
                    >
                      Add tax rate
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
                <div>
                  <div className="flex flex-row space-x-5">
                    <div className="flex-1">
                      <label htmlFor="name" className="text-xbase block mb-1">
                        Tax Name{' '}
                        <span className="text-xs font-light text-pos-red">
                          *
                        </span>
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={tax.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="w-[30%]">
                      <label htmlFor="rate" className="text-xbase block mb-1">
                        Tax Rate{' '}
                        <span className="text-xs font-light text-pos-red">
                          *
                        </span>
                      </label>
                      <div className="relative">
                        <Input
                          name="rate"
                          id="rate"
                          value={tax.rate?.toString()}
                          min={0}
                          max={100}
                          type="number"
                          onChange={handleInputChange}
                          className="sale-quantity"
                        />
                        <span className="right-3 absolute top-0 my-auto z-50 bottom-0 text-base font-medium inline-flex items-center">
                          %
                        </span>
                      </div>
                    </div>
                  </div>

                  {validator.message('name', tax.name, 'required|min:2', {
                    messages: {
                      required: 'Tax name is required.',
                    },
                    className:
                      'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                  })}

                  {validator.message(
                    'rate',
                    tax.rate,
                    'required|numeric|min:0,num|max:100,num',
                    {
                      messages: {
                        required: 'Tax rate is required.',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="text-xbase block mb-1"
                  >
                    Description
                  </label>
                  <TextBox
                    id="description"
                    name="description"
                    value={tax.description}
                    rows={3}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className="w-full text-right h-fit shrink-0 flex flex-row justify-center tablet:justify-end ml-auto space-x-4 
          bg-pos-yellow px-5 py-7 rounded-b-md absolute bottom-0"
          >
            <Button
              label={selectedTax ? 'Update' : 'Save'}
              onClick={handleTaxAdd}
              type="button"
              disabled={status === 'LOADING'}
              loading={status === 'LOADING'}
              className="!bg-pos-black border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
              items-center justify-center !text-pos-yellow text-xbase shrink-0 font-medium 
              shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)]"
            />
          </div>
        </form>
      </div>
    </Modal>
  );
}

TaxAdd.defaultProps = {
  tax: undefined,
};

export default TaxAdd;
